import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSignupStatus } from '../../lib/signup/signupApi';
import AuthLayout from '../../components/auth/AuthLayout';
import OTPInput from '../../components/auth/OTPInput';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiRefreshCw,
  FiMail,
  FiArrowRight
} = FiIcons;

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000; // 2 minutes — webhook lag headroom
const OTP_COUNTDOWN_SECONDS = 10 * 60;

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Post-checkout landing page (docs/signup.md §3).
 *
 * Stripe redirects here with ?email=…&session_id=… — the account may NOT
 * exist yet (webhook lag), so we poll /api/signup/status until the webhook
 * finishes provisioning, then dispatch the OTP and complete verification
 * exactly like Login. Defined failure paths:
 *   - payment_failed  → Stripe says the session never got paid → restart CTA
 *   - poll timeout    → webhook stuck → support CTA, never a silent hang
 *   - missing params  → user landed here without checkout → back to /signup
 */
const SignupSuccess = () => {
  const { requestEmailOTP, verifyEmailOTP, authError, clearAuthError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emailParam = (searchParams.get('email') || '').trim().toLowerCase();
  const sessionId = (searchParams.get('session_id') || '').trim();

  // 'polling' | 'ready' | 'payment_failed' | 'timeout' | 'invalid'
  const [phase, setPhase] = useState(
    isValidEmail(emailParam) ? 'polling' : 'invalid'
  );
  const [orgInfo, setOrgInfo] = useState(null);
  const [pollSeconds, setPollSeconds] = useState(0);

  // OTP step state (after ready)
  const [otpId, setOtpId] = useState(null);
  const [digits, setDigits] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [resentNotice, setResentNotice] = useState(false);

  const pollStartedAt = useRef(Date.now());

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ── Status polling until the webhook provisions the account ─────────────
  const tick = useCallback(async () => {
    if (phase !== 'polling') return;
    try {
      const data = await getSignupStatus(emailParam, sessionId);
      if (data.status === 'ready') {
        setOrgInfo(data.organization || null);
        setPhase('ready');
        return;
      }
      if (data.status === 'payment_failed') {
        setPhase('payment_failed');
        return;
      }
      if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
        setPhase('timeout');
        return;
      }
      setPollSeconds(Math.round((Date.now() - pollStartedAt.current) / 1000));
    } catch {
      // Network hiccup / service restart — keep polling until the timeout.
      if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
        setPhase('timeout');
      }
    }
  }, [phase, emailParam, sessionId]);

  useEffect(() => {
    if (phase !== 'polling') return undefined;
    const t = setInterval(tick, POLL_INTERVAL_MS);
    tick(); // immediate first check
    return () => clearInterval(t);
  }, [phase, tick]);

  // ── On ready: dispatch the OTP welcome/login email ──────────────────────
  useEffect(() => {
    if (phase !== 'ready' || otpId) return;
    let cancelled = false;
    (async () => {
      try {
        const { otpId: id } = await requestEmailOTP(emailParam);
        if (!cancelled) {
          setOtpId(id);
          setCountdown(OTP_COUNTDOWN_SECONDS);
        }
      } catch {
        if (!cancelled) setOtpError('Could not send the verification email. Use Resend below.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, otpId, emailParam, requestEmailOTP]);

  // OTP expiry countdown
  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setInterval(() => setCountdown((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown > 0]);

  const handleVerify = async (code) => {
    if (!otpId) return;
    setOtpError(null);
    clearAuthError();
    setSubmitting(true);
    try {
      await verifyEmailOTP(otpId, code);
    } catch {
      setDigits([]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (countdown > OTP_COUNTDOWN_SECONDS - 30) return;
    clearAuthError();
    setResending(true);
    try {
      const { otpId: newOtpId } = await requestEmailOTP(emailParam);
      setOtpId(newOtpId);
      setDigits([]);
      setResentNotice(true);
      setCountdown(OTP_COUNTDOWN_SECONDS);
      setTimeout(() => setResentNotice(false), 4000);
    } catch {
      // authError surfaced by context
    } finally {
      setResending(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ── Renders ──────────────────────────────────────────────────────────────

  if (phase === 'invalid') {
    return (
      <AuthLayout title="Almost there…" subtitle="This page needs a checkout session.">
        <div className="space-y-4">
          <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-start gap-2.5">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
              You've reached the signup confirmation page without completing checkout. Head back
              to signup to choose your plan.
            </p>
          </div>
          <Link
            to="/signup"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
          >
            Back to Signup
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'payment_failed') {
    return (
      <AuthLayout title="Payment not completed" subtitle="No account has been created.">
        <div className="space-y-4">
          <div className="p-3.5 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
              The payment for this checkout session didn't go through (declined or incomplete).
              No payment beyond any Stripe authorization has been taken and no account was created.
              You can safely retry signup with a different card.
            </p>
          </div>
          <Link
            to="/signup"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
          >
            Retry Signup
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'timeout') {
    return (
      <AuthLayout title="Taking longer than expected" subtitle="Your payment was received.">
        <div className="space-y-4">
          <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-start gap-2.5">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
              We received your payment but your workspace hasn't finished provisioning yet
              (checked for over 2 minutes). This is rare — usually a temporary delay on our side.
              Please contact support and quote this reference: <span className="font-mono font-bold">{sessionId || emailParam}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              pollStartedAt.current = Date.now();
              setPhase('polling');
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            Check Again
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'polling') {
    return (
      <AuthLayout title="Setting up your workspace…" subtitle="Payment received — one moment.">
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
              </span>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Creating your organization and owner account (checked for {pollSeconds}s)…
              </p>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2.5 leading-relaxed">
              This usually takes a few seconds. Keep this page open — we'll continue automatically
              the moment your account is ready.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // phase === 'ready' — account provisioned; OTP verification
  return (
    <AuthLayout
      title="Your workspace is ready 🎉"
      subtitle="Verify your email to sign in."
      footer={
        <span>
          Need a new code?{' '}
          <button type="button" onClick={handleResend} className="font-bold text-blue-600 dark:text-blue-400 hover:underline">
            Resend
          </button>
        </span>
      }
    >
      <div className="space-y-5">
        <div className="p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 flex items-start gap-2.5">
          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-emerald-700 dark:text-emerald-300 leading-relaxed">
            <strong>{orgInfo?.name || 'Your organization'}</strong> is live on the{' '}
            <strong>{orgInfo?.plan || 'selected'} plan</strong>
            {orgInfo?.subscription_status ? ` (${orgInfo.subscription_status})` : ''}. We've emailed
            a 6-digit code to <strong>{emailParam}</strong> to activate your Owner account.
          </p>
        </div>

        {(otpError || authError) && (
          <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">{otpError || authError}</p>
          </div>
        )}

        {resentNotice && (
          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 flex items-start gap-2.5">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-emerald-700 dark:text-emerald-300">A new code has been sent to {emailParam}.</p>
          </div>
        )}

        {!otpId ? (
          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-start gap-2.5">
            <SafeIcon icon={FiMail} className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0 animate-pulse" />
            <p className="text-xs text-slate-600 dark:text-slate-300">Sending your verification code…</p>
          </div>
        ) : (
          <>
            <OTPInput
              value={digits}
              onChange={setDigits}
              onComplete={handleVerify}
              disabled={submitting}
              hasError={Boolean(authError || otpError)}
            />

            <button
              type="button"
              onClick={() => handleVerify(digits.join(''))}
              disabled={submitting || digits.join('').length < 6}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Verifying...' : 'Verify & Enter Workspace'}
            </button>

            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                {countdown > 0 ? (
                  <>Expires in <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{formatTime(countdown)}</span></>
                ) : (
                  <span className="text-red-500 font-semibold">Code expired</span>
                )}
              </span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || countdown > OTP_COUNTDOWN_SECONDS - 30}
                className="flex items-center gap-1.5 font-bold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Resending...' : 'Resend Code'}
              </button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default SignupSuccess;
